import { Helmet } from 'react-helmet-async';
import React from "react";

const SITE_URL = 'https://www.mobilityinfotechlogistics.com';
const SITE_NAME = 'Mobility Infotech Logistics';
const DEFAULT_TITLE = 'Mobility Infotech Logistics | AI-Powered Logistics Solutions';
const DEFAULT_DESCRIPTION = 'Optimize transportation, delivery, routing, and supply chain operations with Mobility Infotech Logistics.';
const NO_INDEX_PATHS = new Set(['/404', '/thank-you', '/demo-form']);

const absoluteUrl = (value) => {
  if (!value) return '';
  if (/^https?:\/\//i.test(value)) return value;
  return `${SITE_URL}${value.startsWith('/') ? value : `/${value}`}`;
};

const structuredData = (value) => {
  if (!value) return null;

  try {
    return JSON.stringify(typeof value === 'string' ? JSON.parse(value) : value);
  } catch (error) {
    return null;
  }
};

const SEO = ({ seoData, globalSchema, pathname = '/' }) => {
  const seo = seoData || {};
  const schema = globalSchema || {};
  const normalizedPath = pathname === '/' ? '/' : pathname.replace(/\/+$/, '');
  const canonical = absoluteUrl(seo.canonical_tag) || `${SITE_URL}${normalizedPath}`;
  const title = seo.title || DEFAULT_TITLE;
  const description = seo.description || DEFAULT_DESCRIPTION;
  const image = absoluteUrl(seo.og_image || seo.twitter_image);
  const schemas = [
    seo.faq_schema,
    seo.article_schema,
    ...(Array.isArray(seo.additional_schemas) ? seo.additional_schemas.map((item) => item?.schema) : []),
    schema.review_schema,
    schema.organisation_schema,
    ...(Array.isArray(schema.additional_schemas) ? schema.additional_schemas.map((item) => item?.schema) : []),
  ].map(structuredData).filter(Boolean);

  return (
    <Helmet>
      <html lang="en" />
      <title>{title}</title>
      {seo.keywords && <meta name="keywords" content={seo.keywords} />}
      <meta name="description" content={description} />
      <meta name="robots" content={NO_INDEX_PATHS.has(normalizedPath) ? 'noindex, nofollow' : 'index, follow, max-image-preview:large'} />
      <link rel="canonical" href={canonical} />

      <meta property="og:locale" content={seo.og_locale || 'en_US'} />
      <meta property="og:type" content={seo.og_type || 'website'} />
      <meta property="og:title" content={seo.og_title || title} />
      <meta property="og:description" content={seo.og_description || description} />
      <meta property="og:url" content={absoluteUrl(seo.og_url) || canonical} />
      <meta property="og:site_name" content={schema.og_site_name || SITE_NAME} />
      {image && <meta property="og:image" content={image} />}

      <meta name="twitter:card" content={schema.twitter_card || 'summary_large_image'} />
      {schema.twitter_site && <meta name="twitter:site" content={schema.twitter_site} />}
      <meta name="twitter:title" content={seo.twitter_title || title} />
      <meta name="twitter:description" content={seo.twitter_description || description} />
      {image && <meta name="twitter:image" content={absoluteUrl(seo.twitter_image) || image} />}
      {schema.twitter_creator && <meta name="twitter:creator" content={schema.twitter_creator} />}

      {schemas.map((schema, index) => (
        <script type="application/ld+json" key={index}>{schema}</script>
      ))}
    </Helmet>
  );
};

export default SEO;
